import { CodeText, EmptyState } from "@/components/common";
import { cn } from "@/lib/utils";

import type { SocialPostGenerationTask } from "../types";
import { formatDate } from "../utils";
import { TaskStatusBadge } from "./task-status-badge";

export function PlannerTaskList({
  tasks,
  selectedTaskId,
  onSelect,
  scheduledPostLabels,
}: {
  tasks: SocialPostGenerationTask[];
  selectedTaskId: string | null;
  onSelect: (taskId: string) => void;
  scheduledPostLabels?: Map<string, string>;
}) {
  if (tasks.length === 0) {
    return (
      <EmptyState
        title="No attempts yet"
        description="Start a sandbox run to inspect generated creative and execution details."
        className="min-h-56 rounded-2xl bg-muted/20"
      />
    );
  }

  return (
    <div className="max-h-[calc(100vh-10rem)] space-y-2 overflow-auto pr-1">
      {tasks.map((task) => {
        const isSelected = task.id === selectedTaskId;
        const scheduledPostLabel =
          task.scheduledPostId && scheduledPostLabels
            ? (scheduledPostLabels.get(task.scheduledPostId) ?? null)
            : null;
        return (
          <button
            key={task.id}
            type="button"
            onClick={() => onSelect(task.id)}
            className={cn(
              "group relative w-full overflow-hidden rounded-2xl border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-sm",
              isSelected && "border-foreground/25 bg-muted/40 shadow-sm",
            )}
          >
            {isSelected ? (
              <span className="absolute inset-y-3 left-0 w-1 rounded-r-full bg-foreground" />
            ) : null}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1.5 pl-1">
                <p className="line-clamp-2 text-sm font-semibold leading-5 text-foreground">
                  {task.request.sourceBrief.origin === "direct"
                    ? task.request.sourceBrief.direction
                    : task.request.sourceBrief.origin === "standalone_batch"
                      ? task.request.sourceBrief.batchDirection
                      : task.request.sourceBrief.slot.concept}
                </p>
                <CodeText className="block text-[11px]">
                  {task.request.platform} / {task.request.postType}
                </CodeText>
                {scheduledPostLabel ? (
                  <p className="truncate text-xs text-muted-foreground">
                    Planned Post: {scheduledPostLabel}
                  </p>
                ) : null}
              </div>
              <TaskStatusBadge status={task.status} />
            </div>
            <div className="mt-4 flex items-center justify-between gap-4 border-t pt-3 text-xs text-muted-foreground">
              <span>{formatDate(task.createdAt)}</span>
              <CodeText className="rounded-full bg-muted px-2 py-0.5">
                #{task.id.slice(0, 8)}
              </CodeText>
            </div>
          </button>
        );
      })}
    </div>
  );
}
